import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { navGroups } from "@/lib/nav-data";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguageTransition } from "@/components/LanguageTransitionProvider";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

/**
 * Full-viewport dark drawer for < 1024px. Accordions for grouped menus,
 * plain links for direct routes.
 *
 * Accepts `localisedGroups` from the parent so translations are consistent
 * with the desktop nav. Falls back to English `navGroups` if not provided.
 */
export function MobileNav({ open, onClose, localisedGroups }) {
  const location = useLocation();
  const { t } = useT("nav");
  const locale = useLocale();
  const { start } = useLanguageTransition();
  const groups = localisedGroups || navGroups;

  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleTransitionStart = ({ target, path }) => {
    onClose();
    start({ target, path });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-surface-void"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      data-testid="mobile-nav-drawer"
    >
      <div className="container-frac flex h-[60px] items-center justify-between">
        <Link
          to={addLocalePrefix("/", locale)}
          aria-label="Frac home"
          className="inline-flex items-center"
          data-testid="mobile-nav-logo"
        >
          <img
            src="/brand/frac-logo-white.png"
            alt="Frac"
            width={130}
            height={28}
            className="h-7 w-auto"
            data-testid="mobile-nav-logo-img"
          />
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to={addLocalePrefix("/contact", locale)}
            className="inline-flex items-center rounded-full bg-frac-violet px-4 py-2 text-sm font-semibold text-frac-white hover:bg-[#8419e6] transition-colors duration-base ease-out"
            data-testid="mobile-nav-book-demo"
          >
            {t("bookDemo")}
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("closeMenu")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-primary hover:bg-white/5 transition-colors duration-fast ease-out"
            data-testid="mobile-nav-close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="container-frac flex-1 overflow-y-auto pb-8">
        {/* Language toggle at the top of the drawer */}
        <div className="flex items-center justify-end border-b border-hairline pb-4 pt-1">
          <LanguageToggle
            variant="on-dark"
            onTransitionStart={handleTransitionStart}
            testIdPrefix="mobile-lang-toggle"
          />
        </div>

        <ul className="divide-y divide-hairline">
          {groups.map((g) =>
            g.items ? (
              <MobileAccordion key={g.id} group={g} onClose={onClose} />
            ) : (
              <li key={g.id}>
                <Link
                  to={g.href}
                  className="flex items-center justify-between py-5 text-xl font-semibold text-ink-primary hover:text-frac-violet transition-colors duration-fast ease-out"
                  data-testid={`mobile-nav-link-${g.id}`}
                >
                  {g.label}
                </Link>
              </li>
            ),
          )}
        </ul>
      </div>
    </div>
  );
}

function MobileAccordion({ group, onClose }) {
  const [open, setOpen] = React.useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-5 text-left text-xl font-semibold text-ink-primary transition-colors duration-fast ease-out hover:text-frac-violet"
        data-testid={`mobile-nav-accordion-${group.id}`}
      >
        <span>{group.label}</span>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-ink-tertiary transition-transform duration-fast ease-out",
            open && "rotate-180 text-frac-violet",
          )}
          strokeWidth={1.75}
        />
      </button>
      {open && (
        <ul className="mb-4 grid gap-1 pl-1">
          {group.items.map((item, i) => (
            <li key={item.href}>
              <Link
                to={item.href}
                onClick={onClose}
                className="block rounded-md px-3 py-3 text-sm text-ink-secondary hover:bg-white/5 hover:text-ink-primary transition-colors duration-fast ease-out"
                data-testid={`mobile-nav-item-${group.id}-${i}`}
              >
                <span className="block font-semibold text-ink-primary">
                  {item.label}
                </span>
                {item.desc && (
                  <span className="mt-0.5 block text-xs text-ink-tertiary">
                    {item.desc}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default MobileNav;
